/**
 * StatsChartModal - Modal popup displaying player stats bar chart
 * Mobile: slides up as bottom sheet. Desktop: centered modal.
 * Controls above chart: stat type pills, over/under toggle, slider + +/- buttons
 */

import { useScrollOverflow } from '@hooks/useScrollOverflow';
import type { LeagueKey } from '@parlay-picker/shared/league';
import { isAlternateChartLine } from '@parlay-picker/shared/lineOutcome';
import { getStatValue, PRESEASON_GAME_FOOTNOTE } from '@parlay-picker/shared/statsChart';
import { findTeam } from '@parlay-picker/shared/teams';
import { cn } from '@utils/cn';
import { usePlayerHeadshotUrl } from '@views/picks/utils';
import { AnimatePresence, motion } from 'framer-motion';
import { ClipboardList, X } from 'lucide-react';
import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RosterIntelPanel } from './RosterIntelPanel';
import StatsBarChart, { type GameData } from './StatsBarChart';

interface StatsChartModalProps {
  onClose: () => void;
  playerName: string;
  prop: string;
  recentGames?: GameData[];
  h2hGames?: GameData[];
  propLine?: number;
  statKey?: string;
  opponentAbbr?: string;
  overUnder?: string | null;
  teamName?: string;
  odds?: string;
  opponentDefRank?: number;
  /** Market key for period-specific charts (e.g., 'player_points_q1') */
  marketKey?: string;
  /** League key — used for team lookups */
  leagueKey: LeagueKey;
}

// Available stat types with display labels
const STAT_OPTIONS = [
  { key: 'pts', label: 'PTS' },
  { key: 'reb', label: 'REB' },
  { key: 'ast', label: 'AST' },
  { key: 'fg3m', label: '3PM' },
  { key: 'stl', label: 'STL' },
  { key: 'blk', label: 'BLK' },
  { key: 'pra', label: 'PRA' },
  { key: 'pr', label: 'P+R' },
  { key: 'pa', label: 'P+A' },
  { key: 'ra', label: 'R+A' },
] as const;

// Slider ranges per stat type [min, max]
const LINE_RANGES: Record<string, [number, number]> = {
  pts: [5.5, 45.5],
  reb: [1.5, 18.5],
  ast: [1.5, 14.5],
  fg3m: [0.5, 8.5],
  stl: [0.5, 4.5],
  blk: [0.5, 4.5],
  pra: [15.5, 60.5],
  pr: [12.5, 50.5],
  pa: [12.5, 50.5],
  ra: [5.5, 25.5],
  bs: [0.5, 6.5],
};

// Default lines per stat type
const DEFAULT_LINES: Record<string, number> = {
  pts: 25.5,
  reb: 8.5,
  ast: 7.5,
  fg3m: 2.5,
  stl: 1.5,
  blk: 1.5,
  pra: 35.5,
  pr: 30.5,
  pa: 30.5,
  ra: 15.5,
  bs: 2.5,
};

/** Compute average stat value from recent games, rounded to nearest 0.5 */
function computeAvgLine(games: GameData[] | undefined, statKey: string): number | null {
  if (!games || games.length === 0) return null;
  const played = games.filter((g) => !g.dnp);
  if (played.length === 0) return null;
  const sum = played.reduce((acc, g) => acc + getStatValue(g, statKey), 0);
  const avg = sum / played.length;
  return Math.round(avg * 2) / 2;
}

function getTeamLogo(teamName: string | undefined, leagueKey?: LeagueKey): string | undefined {
  if (!teamName || !leagueKey) return undefined;
  const team = findTeam(teamName, leagueKey);
  return team?.logo || undefined;
}

export default function StatsChartModal({
  onClose,
  playerName,
  prop,
  recentGames,
  h2hGames,
  propLine: initialPropLine,
  statKey: initialStatKey,
  opponentAbbr,
  overUnder: initialOverUnder,
  teamName,
  odds,
  opponentDefRank,
  marketKey,
  leagueKey,
}: StatsChartModalProps): ReactElement | null {
  const [selectedStat, setSelectedStat] = useState<string>(initialStatKey || 'pts');
  const [selectedLine, setSelectedLine] = useState<number>(
    initialPropLine ||
      computeAvgLine(recentGames, initialStatKey || 'pts') ||
      DEFAULT_LINES[initialStatKey || 'pts'],
  );
  const [selectedOverUnder, setSelectedOverUnder] = useState<'over' | 'under'>(
    initialOverUnder?.toLowerCase() === 'under' ? 'under' : 'over',
  );
  const [headshotError, setHeadshotError] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [showRosterIntel, setShowRosterIntel] = useState(false);
  const statSelectorRef = useRef<HTMLDivElement>(null);
  const { canScrollRight: statSelectorOverflows } = useScrollOverflow(statSelectorRef);

  const headshotUrl = usePlayerHeadshotUrl(playerName, leagueKey);

  // Derive team abbreviation for team intel panel
  const teamAbbr = useMemo(() => {
    if (!teamName || !leagueKey) return undefined;
    const team = findTeam(teamName, leagueKey);
    return team?.abbr;
  }, [teamName, leagueKey]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleStatChange = useCallback(
    (newStat: string): void => {
      setSelectedStat(newStat);
      setSelectedLine(computeAvgLine(recentGames, newStat) || DEFAULT_LINES[newStat] || 10.5);
    },
    [recentGames],
  );

  const handleSliderChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = parseFloat(e.target.value);
    setSelectedLine(Math.round(value * 2) / 2);
  }, []);

  const [minLine, maxLine] = LINE_RANGES[selectedStat] || LINE_RANGES.pts;
  const teamLogoUrl = getTeamLogo(teamName, leagueKey);

  return (
    <AnimatePresence>
      <div
        data-testid="stats-chart-modal"
        className="fixed inset-0 z-60 flex items-end sm:items-center justify-center"
      >
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal — bottom sheet on mobile, centered on desktop */}
        <motion.div
          initial={{ y: '100%', opacity: 0.8 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 35 }}
          className={cn(
            'relative bg-theme-surface border border-theme-border shadow-2xl w-full',
            // Mobile: bottom sheet with rounded top
            'rounded-t-2xl sm:rounded-xl',
            // Desktop: centered with max width
            'sm:max-w-lg sm:mx-4',
            // Max height with scroll
            'max-h-[90vh] overflow-y-auto overscroll-contain',
          )}
        >
          {/* Drag handle (mobile only) */}
          <div className="flex justify-center pt-2 pb-0 sm:hidden">
            <div className="w-10 h-1 rounded-full bg-theme-surface-active" />
          </div>

          <div className="p-4 sm:p-5 space-y-4">
            {/* Header with player headshot and team logo */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative shrink-0">
                  {headshotUrl && !headshotError ? (
                    <img
                      src={headshotUrl}
                      alt={playerName}
                      className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover bg-theme-surface-hover"
                      onError={() => setHeadshotError(true)}
                    />
                  ) : (
                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-theme-surface-hover flex items-center justify-center text-lg font-bold text-theme-text-tertiary">
                      {playerName
                        ?.split(' ')
                        .map((n) => n[0])
                        .join('')
                        .slice(0, 2)}
                    </div>
                  )}
                  {teamLogoUrl && !logoError && (
                    <img
                      src={teamLogoUrl}
                      alt={teamName || ''}
                      className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-theme-surface border border-theme-border object-contain"
                      onError={() => setLogoError(true)}
                    />
                  )}
                </div>
                <div className="min-w-0">
                  <h3 className="text-base sm:text-lg font-bold text-theme-text truncate">
                    {playerName}
                  </h3>
                  <p className="text-xs sm:text-sm text-theme-text-tertiary truncate">{prop}</p>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {teamAbbr && (
                  <button
                    type="button"
                    onClick={() => setShowRosterIntel(true)}
                    className="p-2 rounded-lg hover:bg-theme-surface-hover text-theme-text-tertiary hover:text-theme-text transition"
                    title="Roster intel"
                  >
                    <ClipboardList className="w-5 h-5" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-theme-surface-hover text-theme-text-tertiary hover:text-theme-text transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Stat Type Selector — horizontal scroll with animated indicator */}
            <div className="relative">
              {statSelectorOverflows && (
                <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-gray-800 to-transparent z-10 pointer-events-none" />
              )}
              <div
                ref={statSelectorRef}
                className="flex overflow-x-auto scrollbar-none gap-1 -mx-1 px-1"
              >
                {STAT_OPTIONS.map(({ key, label }) => {
                  const isActive = selectedStat === key;
                  return (
                    <button
                      type="button"
                      key={key}
                      onClick={() => handleStatChange(key)}
                      className={cn(
                        'relative px-2.5 py-1.5 text-xs font-semibold rounded-md transition-colors z-10 shrink-0',
                        isActive
                          ? 'text-theme-on-primary-fill'
                          : 'text-theme-text-tertiary hover:text-theme-text-secondary',
                      )}
                    >
                      {isActive && (
                        <motion.div
                          layoutId="activeStatPill"
                          className="absolute inset-0 bg-theme-primary-fill rounded-md"
                          transition={{
                            type: 'spring',
                            stiffness: 400,
                            damping: 30,
                          }}
                        />
                      )}
                      <span className="relative">{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Line Controls — Over/Under + Slider + ±/Reset in one compact section */}
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                {/* Over/Under Toggle */}
                <div className="flex bg-theme-surface-hover rounded-lg p-0.5">
                  <button
                    type="button"
                    onClick={() => setSelectedOverUnder('over')}
                    className={cn(
                      'px-3 py-1.5 text-xs font-medium rounded-md transition',
                      selectedOverUnder === 'over'
                        ? 'bg-theme-success text-theme-text'
                        : 'text-theme-text-tertiary hover:text-theme-text',
                    )}
                  >
                    Over
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedOverUnder('under')}
                    className={cn(
                      'px-3 py-1.5 text-xs font-medium rounded-md transition',
                      selectedOverUnder === 'under'
                        ? 'bg-theme-danger text-theme-text'
                        : 'text-theme-text-tertiary hover:text-theme-text',
                    )}
                  >
                    Under
                  </button>
                </div>

                {/* Line +/- Controls + Reset */}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedLine(Math.max(minLine, selectedLine - 0.5))}
                    disabled={selectedLine <= minLine}
                    className="w-8 h-8 flex items-center justify-center bg-theme-surface-hover text-theme-text rounded-md hover:bg-theme-surface-active disabled:opacity-40 disabled:cursor-not-allowed transition font-medium"
                  >
                    −
                  </button>
                  <div className="bg-theme-primary-fill text-theme-on-primary-fill px-3 py-1.5 rounded-md text-sm font-bold min-w-13 text-center tabular-nums">
                    {selectedLine}
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedLine(Math.min(maxLine, selectedLine + 0.5))}
                    disabled={selectedLine >= maxLine}
                    className="w-8 h-8 flex items-center justify-center bg-theme-surface-hover text-theme-text rounded-md hover:bg-theme-surface-active disabled:opacity-40 disabled:cursor-not-allowed transition font-medium"
                  >
                    +
                  </button>

                  {/* Reset Button */}
                  {initialPropLine &&
                    selectedStat === initialStatKey &&
                    (() => {
                      const initialDirection =
                        initialOverUnder?.toLowerCase() === 'under' ? 'under' : 'over';
                      const lineChanged = selectedLine !== initialPropLine;
                      const directionChanged = selectedOverUnder !== initialDirection;
                      if (!lineChanged && !directionChanged) return null;
                      return (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedLine(initialPropLine);
                            setSelectedOverUnder(initialDirection);
                          }}
                          className="px-2 py-1.5 text-xs bg-theme-surface-hover text-theme-text-secondary rounded-md hover:bg-theme-surface-active transition"
                          title={`Reset to ${initialOverUnder || 'Over'} ${initialPropLine}`}
                        >
                          Reset
                        </button>
                      );
                    })()}
                </div>
              </div>

              {/* Compact slider for quick line jumps */}
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-theme-text-tertiary tabular-nums">{minLine}</span>
                <input
                  type="range"
                  min={minLine}
                  max={maxLine}
                  step={0.5}
                  value={selectedLine}
                  onChange={handleSliderChange}
                  className="flex-1 h-1.5 bg-theme-surface-hover rounded-full appearance-none cursor-pointer accent-theme-primary"
                />
                <span className="text-[10px] text-theme-text-tertiary tabular-nums">{maxLine}</span>
              </div>
            </div>

            {/* Chart */}
            {selectedLine && selectedStat ? (
              <StatsBarChart
                recentGames={recentGames}
                h2hGames={h2hGames}
                propLine={selectedLine}
                isAlternateLine={isAlternateChartLine(
                  marketKey,
                  { line: initialPropLine, statKey: initialStatKey },
                  { line: selectedLine, statKey: selectedStat },
                )}
                statKey={selectedStat}
                opponentAbbr={opponentAbbr}
                overUnder={selectedOverUnder}
                odds={odds}
                opponentDefRank={opponentDefRank}
                leagueKey={leagueKey}
                marketKey={marketKey}
              />
            ) : (
              <div className="bg-theme-bg/50 rounded-lg p-6 text-center text-theme-text-tertiary">
                Unable to display chart for this prop type
              </div>
            )}
            {recentGames?.some((g) => g.isPreseason) && (
              <p className="text-[10px] text-theme-text-tertiary">{PRESEASON_GAME_FOOTNOTE}</p>
            )}
          </div>
        </motion.div>
      </div>

      {/* Roster Intel Panel overlay */}
      {showRosterIntel && teamAbbr && (
        <RosterIntelPanel
          teamAbbr={teamAbbr}
          opponentAbbr={opponentAbbr}
          leagueKey={leagueKey}
          onClose={() => setShowRosterIntel(false)}
        />
      )}
    </AnimatePresence>
  );
}
